<template>
  <section>
    <h3>Service Provider Configuration</h3>

    <p :class="$style.sectionGeneralMargin">
      Set up a custom user provisioning integration with either a SCIM or Custom REST API.
      The type of integration you can create, SCIM or Custom, depends on which API(s)
      this service provider supports.
      <Link
        :href="customImportKbArticleLink"
        :isExternal="true"
        target="_blank"
        text="Learn more."
      />
    </p>

    <!-- Connection Type Chooser-->
    <div
      :class="$style.verticalMargin"
    >
      <label :class="$style.label">API Type*</label>
      <ScimRadioButtonGroup
        id="scimConnectionType"
        :handleOptionChange="handleScimConnectionTypeChange"
        :options="scimConnectionTypeOptions"
        :state="scimConnectionType"
        :usePrettyToggles="true"
      />
    </div>

    <!-- SCIM Connection Type-->
    <transition
      :enterActiveClass="$style.slideEnterActive"
      :enterClass="$style.slideEnter"
      :leaveActiveClass="$style.slideLeaveActive"
      :leaveToClass="$style.slideLeaveTo"
    >
      <div
        v-if="isApiScim"
        :class="$style.verticalMargin"
      >
        <div :class="$style.sectionGeneralMargin">
          <p :class="$style.sectionGeneralMargin">
            A SCIM API integration allows you to automatically provision, update,
            and deprovision users and sync groups in this application from JumpCloud,
            in real-time. It also allows you to manually import users from this
            application into JumpCloud. This type of integration is based on the
            <Link
              href="http://www.simplecloud.info/"
              :isExternal="true"
              target="_blank"
              text="System for Cross-Domain Identity Management"
            />
            (SCIM) standard.
            <Link
              :href="customImportKbArticleLink"
              :isExternal="true"
              target="_blank"
              text="Learn more."
            />
          </p>
          <div :class="$style.sectionGeneralMargin">
            <div :class="$style.strong">
              Before you start:
            </div>
            <ul>
              <li>
                Check that your service provider supports SCIM API key or bearer
                token based authenitcation.
              </li>
              <li>
                Get configuration details from your service provider.
              </li>
            </ul>
          </div>
        </div>
        <label :class="$style.label">SCIM Version*</label>
        <ScimRadioButtonGroup
          id="scimVersion"
          data-test-id="scim-version"
          :handleOptionChange="handleScimVersion"
          :options="scimVersionOptions"
          :state="version"
          :usePrettyToggles="true"
        />
      </div>
    </transition>

    <!-- Authentication Method -->
    <transition
      :enterActiveClass="$style.slideEnterActive"
      :enterClass="$style.slideEnter"
      :leaveActiveClass="$style.slideLeaveActive"
      :leaveToClass="$style.slideLeaveTo"
    >
      <div
        v-if="!isApiScim"
        :class="$style.verticalMargin"
      >
        <div :class="$style.sectionGeneralMargin">
          <p :class="$style.sectionGeneralMargin">
            A custom API integration allows you to manually import users from any
            application that has a custom REST API. This type of integration
            requires you to define the API endpoints and user schema based on the
            REST API documentation provided by this service provider.
          </p>
          <div :class="$style.sectionGeneralMargin">
            <div :class="$style.strong">
              Before you start:
            </div>
            <ul :class="$style.prepList">
              <li>
                Read the support article,
                <Link
                  :href="customImportKbArticleLink"
                  :isExternal="true"
                  target="_blank"
                  text="Import Users from a Custom REST API Integration"
                />
              </li>
              <li>
                Have your SP's API documentation available
              </li>
              <li>
                Understand which authentication method is supported by your SP
              </li>
              <li>
                The supported grant types for the OAuth 2.0 authentication method are
                authorization code and secure token.
                If using this authentication method, ensure the SP supports these grant types.
              </li>
              <li>
                Obtain your credentials
              </li>
            </ul>
          </div>
        </div>
        <label :class="$style.label">Authentication method*</label>
        <div :class="$style.withNotification">
          <ScimRadioButtonGroup
            id="authenticationMethod"
            :handleOptionChange="handleAuthMethodChange"
            :options="authenticationMethodOpts"
            :state="authMethod"
            :usePrettyToggles="true"
          />
          <StatusBadge
            v-if="shouldShowNotification"
            :isUppercase="false"
            status="warning"
            text="New! OAuth 2.0 authentication method"
          />
        </div>

        <transition
          :enterActiveClass="$style.slideEnterActive"
          :enterClass="$style.slideEnter"
          :leaveActiveClass="$style.slideLeaveActive"
          :leaveToClass="$style.slideLeaveTo"
        >
          <!-- Header info -->
          <div
            v-if="isAuthApiKey"
            :class="[$style.inputRow, $style.verticalMargin]"
          >
            <InputValidator
              :config="{
                name: 'header name',
                label: 'Header name*',
                type: 'text',
                validate: 'required',
                value: headerKeyPair.name,
              }"
              @InputValidatorModel="(value) => handleHeaderInputChange(value,'name')"
            />
            <InputValidator
              :config="{
                name: 'header value',
                label: 'Header value*',
                type: 'text',
                validate: 'required',
                value: headerKeyPair.value,
              }"
              @InputValidatorModel="(value) => handleHeaderInputChange(value, 'value')"
            />
          </div>
        </transition>
      </div>
    </transition>

    <ScimSpCredentialsForm
      v-if="!isOAuth2"
      ref="form"
      actionButtonText="test connection"
      :disableActionButton="disableTestButton"
      :errorsList="[]"
      :handleActionButton="handleTestConnection"
      :hideTokenField="isAuthApiKey"
      :loading="loading"
      :showCancelButton="false"
      :showCtaButton="false"
      :showVanityUrl="true"
      :vanityUrl="vanityUrl"
    />
  </section>
</template>

<script>
import { Link, StatusBadge } from '@jumpcloud/ui-components';
import {
  authenticationMethods,
  scimConnectionTypes,
  scimVersions,
} from 'client/util/Constants/ApplicationConstants';
import { mapActions, mapState } from 'vuex';
import { notify } from 'client/util';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import IdentityManagementMixin from 'client/applications/mixins/IdentityManagementMixin';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import ScimRadioButtonGroup from 'client/applications/scim/ScimRadioButtonGroup';
import ScimSpCredentialsForm from 'client/applications/scim/ScimSpCredentialsForm.vue';

const { success, error } = Constants;

const { application } = ItemTypeConsts;
const ApplicationModel = StoreModelMap[application];
const { CUSTOM, SCIM } = scimConnectionTypes;
const { APIKEY, BEARER, OAUTH2 } = authenticationMethods;

const authenticationMethodOpts = [
  { text: 'Bearer token', value: BEARER },
  { text: 'API Key', value: APIKEY },
  { text: 'OAuth 2.0', value: OAUTH2 },
];

const scimConnectionTypeOptions = [
  { text: 'SCIM API', value: SCIM },
  { text: 'Custom API', value: CUSTOM },
];

export default {
  name: 'GenericScimConnectorValidation',

  components: {
    Link,
    InputValidator,
    ScimRadioButtonGroup,
    ScimSpCredentialsForm,
    StatusBadge,
  },

  mixins: [
    IdentityManagementMixin,
  ],

  inject: ['$validator'],

  data() {
    return {
      authenticationMethodOpts,
      disableTestButton: false,
      disableHeaderTestButton: true, /* used in the new UI layout */
      loading: false,
      scimVersionOptions: scimVersions,
      scimConnectionTypeOptions,
    };
  },

  computed: {
    ...mapState(ApplicationModel, {
      isPrebuild: state => !!state.item.provision,
      scimConnectionType: state => state.scimConnectionType,
      authMethod: state => state.authenticationMethod,
      headerKeyPair: state => state.headerKeyPair,
    }),

    customImportKbArticleLink: () => KbArticleUrls.importUsersFromCustomRestApi,

    isApiScim() {
      return this.scimConnectionType === SCIM;
    },

    isAuthApiKey() {
      return this.authMethod === APIKEY;
    },

    shouldShowNotification() {
      const isCustomIntegration = this.scimConnectionType === CUSTOM;
      return !this.isScimActive && isCustomIntegration;
    },

    showVanityUrl() {
      return this.needsVanityUrl || !this.isPrebuild;
    },

    version: {
      get() {
        return this.scimVersion;
      },
      set(val) {
        this.setScimVersion(val);
      },
    },
  },

  methods: {
    ...mapActions(ApplicationModel, {
      setAuthMethod: 'setAuthMethod',
      setScimConnectionType: 'setScimConnectionType',
      verifyScimConnection: 'verifyScimConnection',
      setHeaderKeyPair: 'setHeaderKeyPair',
    }),

    didConnectorChanged(connectionType) {
      let output;
      if (connectionType === CUSTOM) {
        output = this.didCustomApiConfigChanged || this.didOauthConfigChanged;
      }

      if (connectionType === SCIM) {
        output = this.isGenericConnectorChanged
          || this.isGroupsEnabledChanged;
      }

      return output;
    },

    handleAuthMethodChange(method) {
      this.setAuthMethod(method);
    },

    handleHeaderInputChange(value, attribute) {
      const headerCopy = { ...this.headerKeyPair };
      headerCopy[attribute] = value;
      this.setHeaderKeyPair(headerCopy);
    },

    async handleScimConnectionTypeChange(newTypeValue) {
      let allowConnectionTypeChange = true;

      // Store current properties state
      const connectionTypeBefore = this.scimConnectionType;

      // Execute connection type anyways
      this.setScimConnectionType(newTypeValue);

      // Passed previous connection type
      const showModal = this.didConnectorChanged(connectionTypeBefore)
        || this.isScimActive;
      if (showModal) {
        allowConnectionTypeChange = await this.showConnectionTypeChangeWarningModal();
        ModalEventBus.close();

        if (allowConnectionTypeChange) {
          // Restore the opposite option's state to previous saved state
          if (this.scimConnectionType === CUSTOM) {
            this.setScimVersion(this.genericOldData.scimVersion);
          }
          if (this.scimConnectionType === SCIM) this.resetCustomApiConfig();
        } else {
          // Cancelled connection type switch, set the previously saved values
          this.setScimConnectionType(connectionTypeBefore);
        }
      }

      const isScimConType = (this.scimConnectionType === SCIM);
      const isAuthMethodApiKey = (this.authMethod === APIKEY);
      // SCIM uses bearer token only, no capability for custom header
      if (isScimConType && isAuthMethodApiKey) this.setAuthMethod(BEARER);
    },

    handleScimVersion(scimVersion) {
      this.setScimVersion(scimVersion);
    },

    async handleTestConnection() {
      try {
        this.loading = true;
        await this.verifyScimConnection();
        this.loading = false;

        notify(success, 'Connection successful. Follow the next steps to finalize the integration.');
        if (!this.isScimActive) {
          this.disableTestButton = true;
        }
      } catch (e) {
        this.loading = false;
        notify(error, 'There was a problem testing the connection. Please try again.');
      }
    },

    async showConnectionTypeChangeWarningModal() {
      const bodyText = `
        Different information is collected between our SCIM and Custom API connections.
        Some of the data does not transfer to your new selection.
        As a result of switching connection types, you will lose some of the data you’ve entered.
        Are you sure you want to continue?
      `;

      return new Promise((resolve) => {
        ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            headerText: 'Some progress will be lost',
            bodyText,
            mainActionAttention: true,
            handleCancel: () => resolve(false),
            handleContinue: () => resolve(true),
          },
        });
      });
    },
  },
};
</script>

<style module>
  @import '../../css/sections.css';
  @import '../../components/FormValidation/css/formFields.css';
  @import './css/IdentityManagementCommon.css';

  .label {
    margin: 0;
  }

  .scimVersion {
    margin-top: 1rem;
  }

  .strong {
    font-weight: 700;
  }

  .prepList {
    margin-top: 0;
  }

  .verticalMargin {
    margin: 2rem 0;
  }

  .withNotification {
    align-items: center;
    column-gap: 1rem;
    display: flex;
  }
</style>
